import { useState } from "react";

export const useDownload = ({ documentId, resumeData, template, accentColor, editMode, setEditMode, fontFamily, fontScale, getAuthHeaders }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (isDownloading || !resumeData) return;
    setIsDownloading(true);

    // ── [TRACE BUG1:useDownload] Values at download click ────────────────────
    const _docId   = documentId || 'master';
    const _tmplId  = resumeData?._template || '(none)';
    const _resume  = JSON.stringify({
      name:     resumeData?.name,
      template: resumeData?._template,
      id:       resumeData?.id || resumeData?._id,
    });
    console.log(
      `[TRACE BUG1:useDownload] documentId=${JSON.stringify(_docId)}` +
      ` template=${JSON.stringify(_tmplId)}` +
      ` resumeSnapshot=${_resume}`
    );
    // ─────────────────────────────────────────────────────────────────────────

    try {
      const _payload = {
        document_id: documentId || "master",
        format: "pdf",
        profile: "professional",
        resume_data: {
          ...resumeData,
          ...(template ? { _template: template } : {}),
          ...(accentColor ? { _accentColor: accentColor } : {}),
          ...(fontFamily ? { _fontFamily: fontFamily } : {}),
          ...(fontScale ? { _fontScale: fontScale } : {})
        },
        style_override: {
          ...(template ? { template: template } : {}),
          ...(accentColor ? { accent_color: accentColor } : {}),
          ...(fontFamily ? { font: { family: fontFamily, scale: fontScale || 1.0 } } : {})
        }
      };
      console.log(`[TRACE S4:useDownload] POST /api/engine/render payload=`, JSON.stringify(_payload));
      const response = await fetch("/api/engine/render", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          ...getAuthHeaders()
        },
        body: JSON.stringify(_payload),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Server error ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let jobId = null;
      let tmplName = template ? template.replace(/[^a-zA-Z0-9]/g, '_') : 'default';
      let filename = `${resumeData?.name?.replace(/\s+/g, '_') || 'resume'}_${tmplName}.pdf`;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";

        for (const part of parts) {
          const lines = part.split("\n");
          let eventType = "";
          let eventData = "";

          for (const line of lines) {
            if (line.startsWith("event: ")) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith("data: ")) {
              eventData = line.slice(6).trim();
            }
          }

          if (eventType === "engine_ready" && eventData) {
            try {
              const data = JSON.parse(eventData);
              jobId = data.job_id;
              if (data.filename && !data.filename.startsWith("Resume_master")) {
                filename = data.filename;
              }
            } catch (e) {
              console.error("Failed to parse engine_ready data", e);
            }
          } else if (eventType === "engine_error" && eventData) {
            try {
              const data = JSON.parse(eventData);
              throw new Error(data.error || "Rendering failed");
            } catch (e) {
              throw e;
            }
          }
        }
      }

      if (!jobId) {
        throw new Error("Render completed but job_id was not received.");
      }

      // Download file bytes
      const fileRes = await fetch(`/api/engine/file/${jobId}`, {
        headers: { ...getAuthHeaders() }
      });
      if (!fileRes.ok) {
        const err = await fileRes.json().catch(() => ({}));
        throw new Error(err.error || "Failed to download rendered file.");
      }

      const blob = await fileRes.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);

    } catch (err) {
      console.error("[Download Engine] error:", err);
      alert(`PDF download failed: ${err.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadDocx = async () => {
    if (isDownloading || !resumeData) return;
    setIsDownloading(true);
    const candidateName = resumeData?.name?.replace(/\s+/g, '_') || 'resume';
    const filename = `${candidateName}_resume.docx`;
    try {
      const res = await fetch("/api/resume/docx", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({
          resume_data: {
            ...resumeData,
            ...(template ? { _template: template } : {}),
            ...(accentColor ? { _accentColor: accentColor } : {}),
            ...(fontFamily ? { _fontFamily: fontFamily } : {}),
            ...(fontScale ? { _fontScale: fontScale } : {})
          },
          filename,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Server error ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("[DOCX] download error:", err);
      alert(`DOCX generation failed: ${err.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    isDownloading,
    handleDownload,
    handleDownloadDocx,
  };
};
